import Immutable from 'immutable';
import { DocId } from 'records/Doc';
import { Page, PageId, PageList, RawPage } from 'records/Page';
import {
  PageReadyForReview,
  PageReadyForReviewList,
} from 'records/PageReadyForReview';
import {
  PagesReadyForReview,
  RawPagesReadyForReview,
} from 'records/PagesReadyForReview';
import { PublishRequest } from 'records/PublishRequest';
import GQLRequest from 'utils/GQLRequest';

const pageSchema = `
  pageid
  documentid
  projectid
  width
  height
  rendered
  spacesExtracted
  markupsExtracted
  uploader
  uploadTimestamp
  processingRequestid
  documentTitle
  pageLabel
  pageIndex
  reviewedTimestamp
  reviewer
`;

export const getPage = async (id: PageId): Promise<Page> => {
  const resp = await new GQLRequest()
    .query(
      `
      query Page($id: String!) {
        page(id: $id) {
          ${pageSchema}
        }
      }
      `
    )
    .variables({ id })
    .json<{ page: RawPage }>();

  return Page.deserialize(resp.page);
};

export const getPages = async (documentid: DocId): Promise<PageList> => {
  const { pages } = await new GQLRequest()
    .query(
      `
      query Pages($documentid: String!) {
        pages(documentid: $documentid) {
          ${pageSchema}
        }
      }
      `
    )
    .variables({ documentid })
    .json<{ pages: RawPage[] }>();

  return Immutable.List(pages.map(Page.deserialize));
};

export const getPageUrl = async (
  documentid: DocId,
  pageid: PageId
): Promise<string> => {
  const { pageUrl } = await new GQLRequest()
    .query(
      `
      query PageUrl($documentid: String!, $pageid: String!) {
        pageUrl(documentid: $documentid, pageid: $pageid) {
          url
        }
      }
      `
    )
    .variables({ documentid, pageid })
    .json<{ pageUrl: { url: string } }>();

  return pageUrl.url;
};

export const getPageThumbnailUrl = async (
  documentid: DocId,
  pageid: PageId
): Promise<string> => {
  const { pageThumbnailUrl } = await new GQLRequest()
    .query(
      `
      query PageThumbnailUrl($documentid: String!, $pageid: String!) {
        pageThumbnailUrl(documentid: $documentid, pageid: $pageid) {
          url
        }
      }
      `
    )
    .variables({ documentid, pageid })
    .json<{ pageThumbnailUrl: { url: string } }>();

  return pageThumbnailUrl.url;
};

export const getNextPagesReadyForReview = async (
  cursor: string
): Promise<PagesReadyForReview> => {
  const { nextPagesReadyForReview } = await new GQLRequest()
    .query(
      `
      query GetNextPagesReadyForReview($cursor: String!) {
        nextPagesReadyForReview(cursor: $cursor) {
          pages {
            filename
            pageid
            documentid
            projectid
            pageLabel
            pageIndex
            documentTitle
            width
            height
            inferredDisciplineDesignator
            inferredPrimaryDiscipline
            inferredSecondaryDiscipline
            inferredsheetnumber
            inferredsheettitle
          }
          cursor
        }
      }
      `
    )
    .variables({ cursor })
    .json<{ nextPagesReadyForReview: RawPagesReadyForReview }>();

  return PagesReadyForReview.deserialize(nextPagesReadyForReview);
};

export const getPagesReadyForReview = async (
  processingRequestid: string
): Promise<PageReadyForReviewList> => {
  const { pagesReadyForReview } = await new GQLRequest()
    .query(
      `
      query GetPagesReadyForReview($processingRequestid: String!, $pageSize: Int!) {
        pagesReadyForReview(processingRequestid: $processingRequestid, pageSize: $pageSize) {
          pages {
            filename
            pageid
            documentid
            projectid
            pageLabel
            pageIndex
            documentTitle
            width
            height
            inferredDisciplineDesignator
            inferredPrimaryDiscipline
            inferredSecondaryDiscipline
            inferredsheetnumber
            inferredsheettitle
          }
          cursor
        }
      }
      `
    )
    .variables({ processingRequestid, pageSize: 25 }) // pageSize is hard set in API to 25 for now.
    .json<{ pagesReadyForReview: RawPagesReadyForReview }>();

  let prfrList = Immutable.List(
    pagesReadyForReview.pages.map(PageReadyForReview.deserialize)
  );
  let { cursor } = pagesReadyForReview;

  while (cursor) {
    const { pages: nextPages, cursor: nextCursor } =
      await getNextPagesReadyForReview(cursor);

    prfrList = prfrList.concat(nextPages);
    cursor = nextCursor;
  }

  return prfrList;
};

export const publishPages = async (
  projectid: string,
  publishRequests: PublishRequest[]
): Promise<string[]> => {
  const input = {
    projectid,
    requests: publishRequests.map((pr) => pr.serialize()),
  };

  const {
    publishPages: { publicationids },
  } = await new GQLRequest()
    .query(
      `
      mutation PublishPages($input: PublishRequests!) {
        publishPages(input: $input) {
          publicationids
        }
      }
      `
    )
    .variables({ input })
    .json<{ publishPages: { publicationids: string[] } }>();

  return publicationids;
};

export const deleteUnpublishedPages = async (
  projectid: string,
  documentid: string,
  pages: PageId[]
): Promise<string[]> => {
  const deletedPages = await new GQLRequest()
    .query(
      `
      mutation DeleteUnpublishedPages($projectid: String!, $documentid: String!, $pageids: [String!]!) {
        deleteUnpublishedPages(projectid: $projectid, documentid: $documentid, pageids: $pageids)
      }
      `
    )
    .variables({ projectid, documentid, pageids: pages })
    .json<{ deleteUnpublishedPages: string[] }>();

  return deletedPages.deleteUnpublishedPages;
};

export const deletePublishedPages = async (
  projectid: string,
  documentid: string,
  pages: PageId[]
): Promise<string[]> => {
  const deletedPages = await new GQLRequest()
    .query(
      `
      mutation DeletePublishedPages($projectid: String!, $documentid: String!, $pageids: [String!]!) {
        deletePublishedPages(projectid: $projectid, documentid: $documentid, pageids: $pageids)
      }
      `
    )
    .variables({ projectid, documentid, pageids: pages })
    .json<{ deletePublishedPages: string[] }>();

  return deletedPages.deletePublishedPages;
};

export default {
  getPages,
  getPageUrl,
  getPageThumbnailUrl,
  getPagesReadyForReview,
};
